/**
 * Session-token storage helper.
 *
 * Design goals (per code-review "insecure storage" flag):
 *  1. Prefer httpOnly cookies (`withCredentials: true` on axios already sends
 *     them). The Bearer token is a FALLBACK for cross-domain contexts where
 *     third-party cookies are blocked (iOS Safari + PWAs, Chrome incognito).
 *  2. Never store the token in globals or expose it to any code that doesn't
 *     import this module — keeps the XSS blast radius small.
 *  3. Sanitize inputs before storing so a hostile server can't inject HTML/JS.
 *  4. Provide a `wipe()` used at logout so we don't leak the token in
 *     persisted state.
 *
 * We keep localStorage as the transport since the app is a PWA that must
 * survive a full page reload; sessionStorage would sign users out on every
 * app relaunch. In exchange we accept a strict allow-list on what the key
 * accepts, and we cap the value length.
 */
const KEY = "pb_session_token";
const MAX_LEN = 512;
// A dev-login token is `devtok_<32 hex>`; a normal session token is
// `sess_<24-64 hex>`. Anything else is refused.
const TOKEN_RX = /^[A-Za-z0-9_.\-]{16,512}$/;

export function setToken(raw) {
  if (raw == null) {
    wipeToken();
    return;
  }
  const trimmed = String(raw).trim();
  if (!TOKEN_RX.test(trimmed) || trimmed.length > MAX_LEN) {
    // Refuse to persist anything that doesn't look like a real token so a
    // hostile response can't stuff HTML into localStorage.
    return;
  }
  try {
    localStorage.setItem(KEY, trimmed);
  } catch {
    // Storage disabled (Safari private mode) — the httpOnly cookie is our fallback.
  }
}

export function getToken() {
  try {
    const v = localStorage.getItem(KEY);
    if (!v) return null;
    return TOKEN_RX.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function wipeToken() {
  try { localStorage.removeItem(KEY); } catch { /* no-op */ }
}
